// @ts-strict-ignore
import { SohoModalDialogRef, SohoModalDialogService } from 'ids-enterprise-ng';
import { AppConstants, LMFormType } from '@utils/app.constants';
import { Observable } from 'rxjs';
import { LMModel } from '@form/model';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import {
  LMActionService,
  LMBusyIndicatorService,
  LMErrorUtilService,
  LMLoggerService
} from '@services/index';
import { LMDialogButtonKey } from '@utils/app.constants';
import { LMSelectRecordDialogComponent } from './lm-select-record-dialog.component';
import { LMFormContext } from '@form/services';
import { LMFormTransactionService } from '@form/services/lm-form-transaction.service';
import { Injectable, ViewContainerRef } from '@angular/core';
import { LMTransactionState } from '@transaction/lm-transaction-state';

export enum LMSelectRecordDialogResult {
  RESPONSE_CANCEL = 0,
  RESPONSE_OK
}

export interface LMSelectRecordResult {
  result: LMSelectRecordDialogResult;
  dataView?: LMDataView;
  model?: LMModel;
  isModified?: boolean;
}

export interface LMSelectRecordDialogOptions {
  formUrlInfo: LMFormUrlInfo;
  formSpec: LMFormSpec;
  busFormSpec: LMBusFormSpec;
  dataView: LMDataView;
  fieldId: string;
  placeholder: ViewContainerRef;
}

@Injectable()
export class LMSelectRecordDialogService {
  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmActionService: LMActionService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmFormTransactionService: LMFormTransactionService,
    private lmLoggerService: LMLoggerService,
    private lmUniqueIdService: LMUniqueIdService
  ) {}

  public promptSelectRecordDialog(args: LMSelectRecordDialogOptions): Observable<LMSelectRecordResult> {
    const formSpec: LMFormSpec = args.formSpec;

    const busFormSpec = args.busFormSpec;
    busFormSpec.formType = LMFormType.ACTION;

    const title = formSpec.busFormSpec.title.resourceValue;
    return this.openDialog(args.formUrlInfo, formSpec, args.dataView, args.placeholder, args.fieldId, title);
  }

  private openDialog(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, dataView: LMDataView, placeholder: ViewContainerRef, fieldId: string, title: string): Observable<LMSelectRecordResult> {
    this.lmBusyIndicatorService.showBusyIndicator();
    return new Observable<LMSelectRecordResult>(observer => {
      let dlgComponent: LMSelectRecordDialogComponent;
      let dialog: SohoModalDialogRef<LMSelectRecordDialogComponent>;

      const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
      const formName = formSpec.busFormSpec.name;
      const formContext: LMFormContext = {
        formUrlInfo,
        formSpec,
        placeholder
      };

      // Build the buttons array
      const buttons = [
        {
          id: this.lmUniqueIdService.uniqueId([formNamePrefix, formName, LMDialogButtonKey.CANCEL]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            dialog.close(<LMSelectRecordResult> {
              result: LMSelectRecordDialogResult.RESPONSE_CANCEL
            });
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([formNamePrefix, formName, LMDialogButtonKey.OK]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          validate : false,
          isDefault: true,
          click: (e, modal) => {
            const selectDataView = dlgComponent.formComponent.formModel.getData();
            const actionSpec = this.lmActionService.getActionByName(formSpec.busClassSpec.actions, formSpec.action);
            this.runValidateKeysOnly(formContext, actionSpec, selectDataView).subscribe((validated: boolean) => {
              if (validated) {
                dialog.close(<LMSelectRecordResult> {
                  result:   LMSelectRecordDialogResult.RESPONSE_OK,
                  dataView: dlgComponent.formComponent.formModel.getData(),
                  model:    dlgComponent.formComponent.formModel,
                  isModified: dlgComponent.formComponent.formModel.isDirty
                });
              }
            });
          }
        }
      ];

      dialog = this.sohoModalDialogService
        .modal(LMSelectRecordDialogComponent, placeholder, {fullsize: 'responsive'})
        .buttons(buttons)
        .title(title)
        .isAlert(false)
        .beforeOpen(() => {
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .open();

      dialog.apply((selectRecordDialog: LMSelectRecordDialogComponent) => {
        dlgComponent = selectRecordDialog;

        selectRecordDialog.formOptions = {};
        selectRecordDialog.formUrlInfo = formUrlInfo;
        selectRecordDialog.formSpec = formSpec;
        selectRecordDialog.dataView = dataView;
      });

      dialog.afterClose(result => {
        observer.next(result);
        observer.complete();
      });
    });
  }

  private runValidateKeysOnly(formContext: LMFormContext, actionSpec: LMActionSpec, dataView: LMDataView): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.lmFormTransactionService.runValidateKeysOnly(formContext, actionSpec, dataView).subscribe(
        (transactionState: LMTransactionState) => {
          this.lmLoggerService.log([ this, 'runValidateKeysOnly  {' + actionSpec.name + '}: ' + JSON.stringify(transactionState.getTransactionResult()) ]);
          if (transactionState.isError) {
            this.lmErrorUtilService.handleError(
              transactionState.response.message,
              transactionState.response.details,
              transactionState.response.status,
              transactionState.response.message);
          } else {
            this.handleTransactionStatus(transactionState.getTransactionResult());
          }

          observer.next(transactionState.isSuccess());
          observer.complete();
        });
    });
  }

  private handleTransactionStatus(result: LMActionResult) {
    if (result.status === AppConstants.VIEW_EXCEPTION) {
      this.lmErrorUtilService.handleError(result.message, result.details, result.status);
    } else if (result.status !== AppConstants.STATUS_COMPLETED && result.status !== AppConstants.CONFIRMATION_REQUIRED) {
      this.lmErrorUtilService.handleError(result.message, result.details, result.status, result.message);
    }
  }
}
